/**
 * 历史牌局的文案层。和 `table/cardFormat.ts` 同一个理由单独成文件：
 * 净胜负的符号、时间的显示格式，只要有一处走了味，账目看起来就会前后不一致。
 */
import { actionAmountOf, actionKindOf, actionText, type AppliedActionTypeLike } from '../table/cardFormat.js'
import type { Chips, SeatIndex } from '@poker/shared'

/** 净胜负：符号 + 数字，双重编码到文案里，颜色由调用方用 CSS class 叠加（不单独靠色）。 */
export function formatNet(net: number): string {
  const abs = Math.abs(net).toLocaleString()
  if (net > 0) return `+${abs}`
  if (net < 0) return `−${abs}`
  return '0'
}

export function netTone(net: number): 'win' | 'loss' | 'even' {
  if (net > 0) return 'win'
  if (net < 0) return 'loss'
  return 'even'
}

/**
 * 手牌时间：同一天只显示时:分，跨天带上月/日。历史列表按时间倒序滚很长，
 * 每条都印完整年月日时分秒只会制造噪音，真正要分辨的是「今天这条」与「早前那条」。
 */
export function formatHandTime(ms: number, now: number = Date.now()): string {
  const d = new Date(ms)
  const n = new Date(now)
  const sameDay = d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate()
  return new Intl.DateTimeFormat('zh-CN', sameDay
    ? { hour: '2-digit', minute: '2-digit', hour12: false }
    : { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }
  ).format(d)
}

const FORCED_KINDS: ReadonlySet<string> = new Set(['postBlind', 'postAnte', 'postEntry', 'postDeadBlind'])

/**
 * 一条动作日志的文案，比如「座位 2 加注 200」「老王 补盲 20」「老王 补小盲 10」。
 * `bigBlindSeat` 用来认出入局补盲（引擎里它也是 postBigBlind）。
 */
export function actionLogLine(
  a: {
    readonly type: AppliedActionTypeLike
    readonly seat: SeatIndex
    readonly to: Chips
    readonly delta: Chips
    readonly allIn: boolean
  },
  seatLabel: string,
  bigBlindSeat: SeatIndex,
): string {
  const kind = actionKindOf(a.type, { seat: a.seat, bigBlindSeat })
  // 强制投入（盲注、底注、补盲）照写类别和金额，全下另外标出来：只写「全下」看不出交的是什么、交了多少
  if (FORCED_KINDS.has(kind)) {
    return `${seatLabel} ${actionText(kind, actionAmountOf(a))}${a.allIn ? '（全下）' : ''}`
  }
  return `${seatLabel} ${actionText(a.allIn ? 'allIn' : kind, actionAmountOf(a))}`
}
